/**
 * PendingConsentsBanner
 *
 * Banner no-bloqueante que detecta inscripciones del usuario actual con
 * consentimiento pendiente y muestra una alerta clickeable. Al hacer clic
 * se abre el `ConsentCheckinModal` para esa inscripción.
 *
 * Uso:
 *   <PendingConsentsBanner variant="card" />
 *   <PendingConsentsBanner variant="compact" />  // para drawer/sidebar
 */

import { useEffect, useMemo, useState } from "react";
import { AlertCircle, FileText, ChevronRight } from "lucide-react";
import { useAuthStore } from "@/stores/authStore";
import { eventRegistrationService, eventService } from "@/lib/services/events";
import {
  getConsentById,
  isConsentCompleted,
} from "@/lib/services/eventConsents";
import EventConsentGate from "./EventConsentGate";
import type { EventRegistration } from "@/types";

interface PendingConsentsBannerProps {
  /** Diseño visual: "card" para dashboard/perfil, "compact" para drawer. */
  variant?: "card" | "compact";
  className?: string;
}

interface PendingItem {
  registration: EventRegistration;
}

export function PendingConsentsBanner({
  variant = "card",
  className,
}: PendingConsentsBannerProps) {
  const { profile } = useAuthStore();
  const [pending, setPending] = useState<PendingItem[]>([]);
  const [openEventId, setOpenEventId] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);

  // Escuchar el evento global "consent:updated" disparado por ConsentCheckinModal
  // cuando el usuario sube/completa documentos. Así el badge desaparece sin recargar.
  useEffect(() => {
    const handler = () => setReloadTick((t) => t + 1);
    window.addEventListener("consent:updated", handler);
    return () => window.removeEventListener("consent:updated", handler);
  }, []);

  useEffect(() => {
    let active = true;
    async function load() {
      if (!profile?.personId) {
        setPending([]);
        return;
      }
      try {
        const regs = await eventRegistrationService.getByPerson(
          profile.personId,
        );
        const activeRegs = regs.filter(
          (r) => r.status !== "cancelled" && r.status !== "rejected",
        );
        const results: PendingItem[] = [];
        for (const reg of activeRegs) {
          if (!reg.memberId) continue;
          const [c, evt] = await Promise.all([
            getConsentById(reg.eventId, reg.memberId),
            eventService.getById(reg.eventId),
          ]);
          if (!isConsentCompleted(c, evt?.consentChecklistConfig)) {
            results.push({ registration: reg });
          }
        }
        if (active) setPending(results);
      } catch (err) {
        console.error("[PendingConsentsBanner] load error", err);
      }
    }
    load();
    return () => {
      active = false;
    };
  }, [profile?.personId, reloadTick]);

  const count = pending.length;

  // Render gates en modo manual para todos (uno solo abierto a la vez)
  const gates = useMemo(
    () =>
      pending.map((p) => (
        <EventConsentGate
          key={p.registration.id}
          eventId={p.registration.eventId}
          manual
          open={openEventId === p.registration.eventId}
          onOpenChange={(v) =>
            setOpenEventId(v ? p.registration.eventId : null)
          }
        />
      )),
    [pending, openEventId],
  );

  if (count === 0) return <>{gates}</>;

  if (variant === "compact") {
    return (
      <>
        {gates}
        <button
          onClick={() => setOpenEventId(pending[0].registration.eventId)}
          className={`flex items-center gap-2 w-full px-3 py-2 rounded-lg bg-amber-50 hover:bg-amber-100 border border-amber-200 text-amber-800 text-xs font-medium transition-colors ${className ?? ""}`}
        >
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span className="flex-1 text-left truncate">
            {count === 1
              ? "1 consentimiento pendiente"
              : `${count} consentimientos pendientes`}
          </span>
          <ChevronRight className="w-3.5 h-3.5 shrink-0" />
        </button>
      </>
    );
  }

  return (
    <>
      {gates}
      <div
        className={`relative overflow-hidden rounded-2xl border border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 p-5 shadow-sm ${className ?? ""}`}
      >
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center shadow-md shadow-amber-500/20 shrink-0">
            <FileText className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <AlertCircle className="w-4 h-4 text-amber-700" />
              <p className="text-sm font-bold text-amber-900">
                Tienes{" "}
                {count === 1 ? "un consentimiento" : `${count} consentimientos`}{" "}
                por completar
              </p>
            </div>
            {/* Sin nombrar pasos concretos: cada evento decide cuáles pide
                (consentChecklistConfig), y el texto viejo prometía EPS y
                tratamiento de datos incluso en eventos que los tenían
                apagados. El asistente ya muestra los que apliquen. */}
            <p className="text-xs text-amber-800 mb-3">
              Completa {count === 1 ? "los documentos" : "los documentos de cada evento"}{" "}
              para poder participar en{" "}
              {count === 1 ? "el evento" : "los eventos"}.
            </p>
            <div className="flex flex-col gap-2">
              {pending.map((p) => (
                <button
                  key={p.registration.id}
                  onClick={() => setOpenEventId(p.registration.eventId)}
                  className="flex items-center justify-between gap-3 w-full px-4 py-2.5 bg-white hover:bg-amber-50 border border-amber-200 rounded-xl text-sm font-medium text-amber-900 transition-colors"
                >
                  <span className="truncate text-left">
                    {p.registration.eventName ?? "Evento"}
                  </span>
                  <span className="flex items-center gap-1 text-xs text-amber-700 shrink-0">
                    Completar
                    <ChevronRight className="w-4 h-4" />
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default PendingConsentsBanner;
